import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';

import classNames from 'classnames';
import { Box, useDisclosure } from '@chakra-ui/react';
// OC Card
import OperatorConnectStatus from './OperatorConnectStatus';
import DashboardServiceCard from 'components/v4/Cards/DashboardServiceCard/DashboardServiceCard';
import DashboardServiceCardContent from 'components/v4/Cards/DashboardServiceCard/DashboardServiceCardContent';
import DashboardServiceCardHeader from 'components/v4/Cards/DashboardServiceCard/DashboardServiceCardHeader';
import DashboardServiceCardHeaderActionButton from 'components/v4/Cards/DashboardServiceCard/DashboardServiceCardHeaderActionButton';
import DashboardServiceCardStatusbar from 'components/v4/Cards/DashboardServiceCard/DashboardServiceCardStatusbar';

// Components
import Text from 'components/v4/Text';
import Image from 'components/v4/Image';
import { MenuItem } from 'components/v4';
import ServiceDashboardItemSyncUsers from 'components/v4/Services/ServiceDashboard/ServiceDashboardItemSyncUsers';
import ServiceDashboardTenantId from 'components/v4/Services/ServiceDashboard/ServiceDashboardTenantId';
import { ServiceDashboardItemLastHealthCheck } from '../DirectRouting';
import { IconComponent } from '../IconComponent';
// Util
import { dateStringToDate } from 'utils/utils';

// Assets
import sbcLogo from 'assets/images/sbcaas.png';
import msTeamsLogo from 'assets/images/ms_teams_logo.png';
import path from '@/routes/constants';

import { useServicesDashboard } from 'context/ServicesDashboardContext';
import { OPERATOR_CONNECT } from '../constants';
import { useServiceDashboard } from 'hooks/useCustomQuery/useServiceDashboard';
import { TenantIcon } from './TenantIcon';
import { GeneratePermission } from './GeneratePermission';

export default function OperatorConnectCard({
    service,
    companyAppAuth,
    serviceWizard,
}) {
    const accountScope = useSelector((state) => state.login.requiredScope);
    const [data, setData] = useState(service);
    const [authData, setAuthData] = useState(null);

    const [currentCompanyAppAuth, setCurrentCompanyAppAuth] = useState(null);
    const [actionPermissions, setActionPermissions] = useState({
        menuEditServiceScope: 40,
        menuRequestScope: 60,
    });
    const modalState = useDisclosure();
    const serviceStatus = useServicesDashboard();
    const cardStatus = serviceStatus[OPERATOR_CONNECT];
    const { darkMode } = useSelector((state) => state.settings);

    const {
        data: serviceObj,
        isRefetching,
        isFetching,
        refetch,
    } = useServiceDashboard({
        serviceId: service.id,
        serviceType: OPERATOR_CONNECT,
        enabled: !!service.id,
    });
    const handleGrantAccess = async () => {
        if (serviceObj?.company?.selectiveSyncPolicy === 2) {
            if (serviceWizard.msServiceAuth?.id) {
                await serviceWizard.microsoftAuth();
            } else {
                serviceWizard.ocFlow();
            }
        } else if (
            serviceObj?.company?.selectiveSyncPolicy === 1 ||
            serviceObj?.company?.selectiveSyncPolicy === 0
        ) {
            await serviceWizard.microsoftAuth();
        }
    };

    useEffect(() => {
        if (!(isFetching || isRefetching)) {
            refetch();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (service) serviceWizard.setSelectedServiceProfile(service);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [service]);

    useEffect(() => {
        if (companyAppAuth) {
            //set MS Service Auth & Service Data.
            setAuthData(serviceWizard.msServiceAuth);
            setData({
                ...serviceWizard.msServiceAuth,
                ...service,
                hasAppAuthData: true,
                lastProcessed:
                    serviceWizard.msServiceAuth?.lastProcessedTS &&
                    dateStringToDate(
                        serviceWizard.msServiceAuth?.lastProcessedTS,
                        'datetime',
                    ),
            });
            setCurrentCompanyAppAuth(companyAppAuth);
        }
    }, [companyAppAuth, service, serviceWizard.msServiceAuth]);

    useEffect(() => {
        if (serviceObj) {
            // Tenant ID
            if (
                !serviceWizard?.msServiceAuth?.tenantID &&
                serviceObj?.status === 2 &&
                (serviceObj?.authPolicy === 0 || serviceObj?.authPolicy === 1)
            ) {
                if (serviceObj?.company?.selectiveSyncPolicy !== 2) {
                    setActionPermissions((p) => ({
                        ...p,
                        ...{
                            menuTenantIdScope: 20,
                        },
                    }));
                }
            } else {
                setActionPermissions((p) => {
                    if (
                        Object.prototype.hasOwnProperty.call(
                            p,
                            'menuTenantIdScope',
                        )
                    ) {
                        delete p.menuTenantIdScope;
                        return p;
                    }
                    return p;
                });
            }

            // Provide Access
            if (serviceObj?.authPolicy !== 0) {
                if (
                    (serviceObj?.status === 2 &&
                        currentCompanyAppAuth === null) ||
                    (serviceObj?.status === 2 &&
                        (data.userSync === false ||
                            data.graphAuth === false)) ||
                    currentCompanyAppAuth?.status === 20
                ) {
                    setActionPermissions((p) => ({
                        ...p,
                        ...{
                            menuProvideAccessScope: 20,
                        },
                    }));
                }
            } else {
                setActionPermissions((p) => {
                    if (
                        Object.prototype.hasOwnProperty.call(
                            p,
                            'menuProvideAccessScope',
                        )
                    ) {
                        delete p.menuProvideAccessScope;
                        return p;
                    }
                    return p;
                });
            }

            //TODO: Enable User Sync After Manual App Auth

            if (serviceObj?.authPolicy !== 0) {
                if (
                    serviceWizard?.msServiceAuth?.tenantID &&
                    data.graphAuth === false && //have tenant id & no graph auth
                    data.userSync === false // hide if userSync is enabled
                ) {
                    setActionPermissions((p) => ({
                        ...p,
                        ...{
                            menuEnableSyncScope: 20,
                        },
                    }));
                }
            } else {
                setActionPermissions((p) => {
                    if (
                        Object.prototype.hasOwnProperty.call(
                            p,
                            'menuEnableSyncScope',
                        )
                    ) {
                        delete p.menuEnableSyncScope;
                        return p;
                    }
                    return p;
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentCompanyAppAuth, companyAppAuth, serviceObj, service, data]);

    const classes = classNames({
        flash: service.changes === 'added',
    });

    return (
        <>
            <ServiceDashboardTenantId {...modalState} {...serviceWizard} />
            <DashboardServiceCard
                className={classes}
                dataWalkthroughId={service.name}>
                <DashboardServiceCardStatusbar
                    cardStatus={cardStatus}
                    statusDisplay={
                        cardStatus?.statusDisplay || service.statusDisplay
                    }
                />
                <DashboardServiceCardHeader>
                    <Text fontSize="lg" className="truncate" marginBottom="0">
                        {data.name}
                    </Text>
                    <DashboardServiceCardHeaderActionButton
                        id="operator-connect"
                        dataWalkthroughId={service.name}>
                        {/* Edit Service */}
                        {cardStatus?.showEdit &&
                            accountScope >=
                                actionPermissions.menuEditServiceScope && (
                                <Link
                                    id="edit-oc-service"
                                    className="bg-transparent !no-underline hover:text-inherit"
                                    to={`${path.SERVICES_OPERATOR_CONNECT}/${data.id}/edit`}
                                    state={serviceObj}>
                                    <MenuItem
                                        icon={
                                            <IconComponent
                                                icon="Edit"
                                                color={
                                                    darkMode ? 'white' : 'black'
                                                }
                                            />
                                        }
                                        data-walkthroughid={`/dashboard-services/card/${service.name}/edit-service`}>
                                        <Text marginBottom="0">
                                            Edit Service
                                        </Text>
                                    </MenuItem>
                                </Link>
                            )}
                        {/* Request Resource */}
                        {accountScope >=
                            actionPermissions?.menuRequestScope && (
                            <GeneratePermission
                                serviceObj={serviceObj}
                                serviceScope={
                                    actionPermissions?.menuRequestScope
                                }></GeneratePermission>
                        )}
                        {/* Tenant ID */}
                        {cardStatus?.showTenantId &&
                            serviceObj?.authPolicy !== 2 && (
                                <MenuItem
                                    id="tenant-id"
                                    icon={
                                        <TenantIcon
                                            color={darkMode ? 'white' : 'black'}
                                        />
                                    }
                                    onClick={() => {
                                        modalState.onOpen();
                                    }}
                                    data-walkthroughid={`/dashboard-services/card/${service.name}/tenant-id`}>
                                    <Text marginBottom="0">Tenant ID</Text>
                                </MenuItem>
                            )}

                        {/* Provide Access */}
                        {cardStatus?.grantAccess &&
                            serviceObj?.authPolicy !== 0 && (
                                <MenuItem
                                    id="oc-provide-access"
                                    icon={
                                        <IconComponent
                                            icon="Settings"
                                            color={darkMode ? 'white' : 'black'}
                                        />
                                    }
                                    onClick={handleGrantAccess}
                                    data-walkthroughid={`/dashboard-services/card/${service.name}/provide-access`}>
                                    <Text marginBottom="0">Grant Access</Text>
                                </MenuItem>
                            )}
                    </DashboardServiceCardHeaderActionButton>
                </DashboardServiceCardHeader>
                <DashboardServiceCardContent>
                    <Box>
                        <Image
                            src={
                                service.serviceType === 'SBCaaS'
                                    ? sbcLogo
                                    : msTeamsLogo
                            }
                            sx={{
                                maxWidth: '50px',
                                margin: '0 auto 15px auto',
                            }}
                        />

                        <Box
                            sx={{
                                display: 'flex',
                                flexDir: 'column',
                            }}>
                            {/* Status */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '15px',
                                }}>
                                <Text
                                    sx={{
                                        fontWeight: '500',
                                    }}>
                                    Status
                                </Text>
                                <OperatorConnectStatus
                                    serviceObj={serviceObj}
                                    authData={authData}
                                    handleActions={{
                                        handleGrantAccess,
                                    }}
                                />
                            </Box>

                            {/* Sync Time */}
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: '15px',
                                }}>
                                <Text
                                    sx={{
                                        fontWeight: '500',
                                    }}>
                                    Sync
                                </Text>
                                <Text sx={{ textAlign: 'left' }}>
                                    <ServiceDashboardItemSyncUsers
                                        data={data}
                                    />
                                </Text>
                            </Box>

                            {/* Last Health Check */}
                            <ServiceDashboardItemLastHealthCheck data={data} />
                        </Box>
                    </Box>
                </DashboardServiceCardContent>
            </DashboardServiceCard>
        </>
    );
}
